import { PayloadAction } from '@reduxjs/toolkit';
import createAsyncSlice from '../createAsyncSlice';
import { Answer, OrderType, Question, TagData, TagWithQcnt } from '../../types';
import { getQuestionsByFilter } from '../../services/questionService';
import { getTagByName, getTagsWithQuestionNumber } from '../../services/tagService';

/**
 * Represents the state of the question page.
 */
export interface QuestionsSliceState {
  qlist: Question[] | undefined;
  tlist: TagWithQcnt[] | undefined;
  pageTitle: string;
  searchString: string;
  order: OrderType;
  shouldFetchQuestions: boolean;
  status: 'idle' | 'loading' | 'failed';
}

/**
 * Represents the initial state of the question page.
 */
const initialState: QuestionsSliceState = {
  qlist: [],
  tlist: [],
  pageTitle: 'All Questions',
  searchString: '',
  order: 'newest',
  shouldFetchQuestions: false,
  status: 'idle',
};

/**
 * Creates reducers and selectors with which to used to interact with the question page state.
 */
export const questionsSlice = createAsyncSlice({
  name: 'questionPage',
  initialState,
  reducers: create => ({
    // Updates the state by fetching questions from the database using the order and search term
    fetchQuestions: create.asyncThunk(
      async (questionOrder: string) => {
        const res = await getQuestionsByFilter(questionOrder);
        return res || [];
      },
      {
        pending: state => {
          state.status = 'loading';
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.qlist = action.payload;
        },
        rejected: state => {
          state.status = 'failed';
        },
      },
    ),
    // Updates the state's list of tag data by fetching the tag data from the database
    fetchTags: create.asyncThunk(
      async () => {
        const tdlist = await getTagsWithQuestionNumber();
        const fetchTagWithDescription = async (td: TagData) => {
          const tag = await getTagByName(td.name);
          return tag;
        };
        const taglist = await Promise.all(tdlist.map(td => fetchTagWithDescription(td)));
        return taglist.map(t => ({
          ...t,
          qcnt: tdlist.find(td => td.name === t.name)?.qcnt || 0,
        }));
      },
      {
        pending: state => {
          state.status = 'loading';
        },
        fulfilled: (state, action) => {
          state.status = 'idle';
          state.tlist = action.payload;
        },
        rejected: state => {
          state.status = 'failed';
        },
      },
    ),
    // Updates the state's list of questions based on the given question
    // If the given question's ID is in the list, edits the question in the list with the matching ID
    // If the given question's ID is not in the list, adds the given question to the list
    updateQuestions: create.reducer((state, action: PayloadAction<Question>) => {
      const questionExists = state.qlist?.some(q => q._id === action.payload._id);
      if (questionExists) {
        state.qlist = state.qlist?.map(q => (q._id === action.payload._id ? action.payload : q));
      } else {
        state.qlist = [action.payload, ...(state.qlist || [])];
      }
    }),
    // Updates a question in the state's list of questions with the given answer based on the given ID
    updateAnswer: create.reducer(
      (state, action: PayloadAction<{ qid: string; answer: Answer }>) => {
        state.qlist = state.qlist?.map(q =>
          q._id === action.payload.qid
            ? { ...q, answers: [...q.answers, action.payload.answer] }
            : q,
        );
      },
    ),
    // Update's the state's page title with the given string
    setPageTitle: create.reducer((state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload;
    }),
    // Update's the state's search string with the given string
    setSearchString: create.reducer((state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
    }),
    // Update's the state's question order with the given order
    setOrder: create.reducer((state, action: PayloadAction<OrderType>) => {
      state.order = action.payload;
    }),
    setShouldFetchQuestions: create.reducer((state, action: PayloadAction<boolean>) => {
      state.shouldFetchQuestions = action.payload;
    }),
  }),
  selectors: {
    qlistSelector: state => state.qlist,
    qlistBySearchStringSelector: state => {
      /**
       * Parses tags from a search string.
       *
       * @param {string} search - Search string containing tags in square brackets (e.g., "[tag1][tag2]")
       *
       * @returns {string[]} - An array of tags found in the search string
       */
      const parseTags = (search: string): string[] =>
        (search.match(/\[([^\]]+)\]/g) || []).map(word => word.slice(1, -1));

      /**
       * Parses keywords from a search string by removing tags and extracting individual words.
       *
       * @param {string} search - The search string containing keywords and possibly tags
       *
       * @returns {string[]} - An array of keywords found in the search string
       */
      const parseKeyword = (search: string): string[] =>
        search.replace(/\[([^\]]+)\]/g, ' ').match(/\b\w+\b/g) || [];

      /**
       * Checks if given question contains any tags from the given list.
       *
       * @param {Question} q - The question to check
       * @param {string[]} taglist - The list of tags to check for
       *
       * @returns {boolean} - `true` if any tag is present in the question, `false` otherwise
       */
      const checkTagInQuestion = (q: Question, taglist: string[]): boolean => {
        for (const tagname of taglist) {
          for (const tag of q.tags) {
            if (tagname === tag.name) {
              return true;
            }
          }
        }

        return false;
      };

      /**
       * Checks if any keywords in the provided list exist in a given question's title or text.
       *
       * @param {Question} q - The question to check
       * @param {string[]} keywordlist - The list of keywords to check for
       *
       * @returns {boolean} - `true` if any keyword is present, `false` otherwise.
       */
      const checkKeywordInQuestion = (q: Question, keywordlist: string[]): boolean => {
        for (const w of keywordlist) {
          if (q.title.includes(w) || q.text.includes(w)) {
            return true;
          }
        }

        return false;
      };

      /**
       * Filters questions based on a search string containing tags and/or keywords.
       *
       * @param {Question[]} qlist - The list of questions to filter
       * @param {string} search - The search string containing tags and/or keywords
       *
       * @returns {Question[]} - The filtered list of questions matching the search criteria
       */
      const filterQuestionsBySearch = (qlist: Question[], search: string): Question[] => {
        const searchTags = parseTags(search);
        const searchKeyword = parseKeyword(search);
        if (!qlist || qlist.length === 0) {
          return [];
        }
        return qlist.filter((q: Question) => {
          if (searchKeyword.length === 0 && searchTags.length === 0) {
            return true;
          }

          if (searchKeyword.length === 0) {
            return checkTagInQuestion(q, searchTags);
          }

          if (searchTags.length === 0) {
            return checkKeywordInQuestion(q, searchKeyword);
          }

          return checkKeywordInQuestion(q, searchKeyword) || checkTagInQuestion(q, searchTags);
        });
      };

      return filterQuestionsBySearch(state.qlist || [], state.searchString);
    },
    tlistSelector: state => state.tlist,
    pageTitleSelector: state => state.pageTitle,
    searchStringSelector: state => state.searchString,
    orderSelector: state => state.order,
    shouldFetchQuestionsSelector: state => state.shouldFetchQuestions,
    statusSelector: state => state.status,
  },
});

export const {
  fetchQuestions,
  fetchTags,
  updateQuestions,
  updateAnswer,
  setPageTitle,
  setSearchString,
  setOrder,
  setShouldFetchQuestions,
} = questionsSlice.actions;

export const {
  qlistSelector,
  qlistBySearchStringSelector,
  tlistSelector,
  pageTitleSelector,
  searchStringSelector,
  orderSelector,
  statusSelector,
  shouldFetchQuestionsSelector,
} = questionsSlice.selectors;
